import { AppDispatch } from "./../../app/store";
import {
  TabData,
  netzwirtschaftFieldsData,
  netzwirtschaftState,
  fieldUpdate,
  multiViewType,
  ereignis,
  ereignistyp
} from "./netzwirtschaftTypes";
import { angeschlosseneAnlage, anlagentyp, messgroesse, messstelle, recorder, messwertLog, gespeicherteSuche, netzereignis, pqereignissemessstelle, messwertLogGeflaggt, messwertLogGeflaggtStatus } from "../../utils/apiTypes";

import {
  AnyAction,
  createSlice,
  PayloadAction,
  ThunkAction,
} from "@reduxjs/toolkit";
import { deleteWithAuth, getWithAuth, postJsonWithAuth, postWithAuth } from "../../utils/api";
import type { RootState } from "../../app/store";
import {
  logError,
  exportToCsvLocalTime,
  isDataWithNanoseconds,
  nanosecondsTimeStampToLocalString,
  getYAxisTitleText,
  transformDataToApexAxisChartSeries,
  fillGapsWithNull
} from "../../utils/utils";

import { openSnackbar } from "../../features/snackbar/globalSnackbarSlice";
import { errorHandler } from "../../utils/errorHandling";

const lineColors: Array<string> = [
  "#004754",
  "#bebd00",
  "#229855",
  "#b90000",
  "#83297f",
  "#c66300",
];

const multiViewTabsCount = 4;

const rec: recorder = {
  klasse: "",
  kategorie: "",
  hersteller: "",
  beschreibung: "",
};

const fields: netzwirtschaftFieldsData = {
  uw: "",
  messpunkt: "",
  rekorder: rec,
  messgroesse: Array<messgroesse>(),
  ersteDaten: new Date(0),
  letzteDaten: new Date(0),
  startDatum: new Date(new Date().getTime() - 24 * 60 * 60000),
  endDatum: new Date(),
};

export const emptyTab = (tabIndex: number, tabName = '', resetPicker = true): TabData => {
  const newTab: TabData = {
    name: tabName != '' ? tabName : "Tab " + (tabIndex + 1),
    index: tabIndex,
    fields: fields,
    rekorderOptions: [],
    messgroesseOptions: [],
    graphLoading: false,
    exportLoading: false,
    graphData: {
      yAxisTitleText: "",
      isNanoseconds: false,
      series: [],
      ereignisse: [],
      showEreignisse: false,
      geflaggteMesswerte: [],
      singlePoints: [],
    },
    csvData: [],
    mapBounds: [
      [47.59478215059966, 7.955788378007267],
      [49.54343581533941, 8.627608728542729],
    ],
    showAngeschlosseneAnlagen: true,
    resetDatePicker: resetPicker,
  };
  return newTab;
};

const initialTabs = [emptyTab(0), emptyTab(1)];

const initialState: netzwirtschaftState = {
  selectedTab: 0,
  tabs: initialTabs,
  uwsList: Array<string>(),
  messpunkteSelectList: Array<messstelle>(),
  selectAllMessgroesse: false,
  multiView: multiViewType.single,
  multiViewDisabled: initialTabs.length === 1 || initialTabs.length > multiViewTabsCount,
  anlagentypen: [],
  angeschlosseneAnlagen: [],
  gespeicherteSuchen: [],
  sucheLoading: false,
  listLoading: false,
  umspannwerkLoading: false,
  messpunktLoading: false,
  rekorderLoading: false,
  viewMode: "einzelansicht"
};

const orderOfFields = [
  "uw",
  "messpunkt",
  "rekorder",
  "messgroesse",
  "ersteDaten",
  "letzteDaten",
  "startDatum",
  "endDatum",
];

/* eslint-disable security/detect-object-injection, @typescript-eslint/no-explicit-any */
const setValueAndClear = (oldValues: any, data: fieldUpdate): netzwirtschaftFieldsData => {
  return orderOfFields.reduce(
    (newValues: any, fieldName: string, i: number) => {
      const indexOfChangedField = orderOfFields.indexOf(data.field);
      switch (true) {
        case indexOfChangedField > i: //Felder vor dem geänderten Feld
          newValues[fieldName] = oldValues[fieldName];
          break;
        case indexOfChangedField === i: //geändertes Feld
          newValues[fieldName] = data.value;
          break;
        case indexOfChangedField < i: //Felder nach dem geänderten Feld
          if (fieldName === "messgroesse") {
            newValues[fieldName] = [];
          } else if (fieldName === "startDatum") {
            newValues[fieldName] = oldValues[fieldName];
          } else if (fieldName === "endDatum") {
            new Date(oldValues["endDatum"]) < new Date(newValues["startDatum"])
              ? (newValues[fieldName] = newValues["startDatum"])
              : (newValues[fieldName] = oldValues[fieldName]);
          } else if ((fieldName === "ersteDaten" || fieldName === "letzteDaten") && data.field != "messgroesse") {
            newValues[fieldName] = new Date(0);
          } else if ((fieldName === "ersteDaten" || fieldName === "letzteDaten") && data.field == "messgroesse") {
            newValues[fieldName] = oldValues[fieldName];
          } else if (fieldName == "rekorder") {
            newValues[fieldName] = {
              klasse: "",
              kategorie: "",
              hersteller: "",
              beschreibung: "",
            };
          } else {
            newValues[fieldName] = "";
          }
          break;
      }
      return newValues;
    },
    {}
  );
};
/* eslint-enable security/detect-object-injection, @typescript-eslint/no-explicit-any */

const netzwirtschaftSlice = createSlice({
  name: "netzwirtschaft",
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<number>) => {
      state.selectedTab = action.payload;
    },
    addTab: (state, action: PayloadAction<string>) => {
      state.tabs.push(emptyTab(state.tabs[state.tabs.length - 1].index + 1, action.payload));
      state.selectedTab = state.tabs.length - 1;
      state.multiViewDisabled =
        state.tabs.length === 1 || state.tabs.length > multiViewTabsCount;
      state.multiView = state.multiViewDisabled ? multiViewType.single : state.multiView;
    },
    addTabNotResetDate: (state, action: PayloadAction<string>) => {
      state.tabs.push(emptyTab(state.tabs.length, action.payload, false));
      state.selectedTab = state.tabs.length - 1;
      state.multiViewDisabled =
        state.tabs.length === 1 || state.tabs.length > multiViewTabsCount;
      state.multiView = state.multiViewDisabled ? multiViewType.single : state.multiView;
    },
    deleteTab: (state, action: PayloadAction<number>) => {
      state.tabs.splice(action.payload, 1);
      state.tabs.forEach((e, i) => (e.index = i));
      state.selectedTab =
        state.selectedTab >= action.payload
          ? Math.max(state.selectedTab - 1, 0) //max ist notwendig um den ersten Tab wenn er ausgewählt ist löschen zu können
          : state.selectedTab;
      state.multiViewDisabled =
        state.tabs.length === 1 || state.tabs.length > multiViewTabsCount;
    },
    updateField: (state, action: PayloadAction<fieldUpdate>) => {
      state.tabs[state.selectedTab].fields = setValueAndClear(
        state.tabs[state.selectedTab].fields,
        action.payload
      );
    },
    updateFieldWithPickerResetCheck: (state, action: PayloadAction<fieldUpdate>) => {
      if ((action.payload.field == "startDatum" || action.payload.field == "endDatum") && !state.tabs[state.selectedTab].resetDatePicker) return;
      state.tabs[state.selectedTab].fields = setValueAndClear(
        state.tabs[state.selectedTab].fields,
        action.payload
      );
    },
    setUWS: (state, action: PayloadAction<string[]>) => {
      state.uwsList = action.payload;
    },
    setMesspunkte: (state, action: PayloadAction<messstelle[]>) => {
      state.messpunkteSelectList = action.payload;
    },
    setRekorderOptionsForTab: (state, action: PayloadAction<recorder[]>) => {
      state.tabs[state.selectedTab].rekorderOptions = action.payload;
    },
    setMessgroesseOptionsForTab: (
      state,
      action: PayloadAction<messgroesse[]>
    ) => {
      state.tabs[state.selectedTab].messgroesseOptions = action.payload;
    },
    setGraphDataForTab: (state, action) => {
      const selectedTab = state.tabs[state.selectedTab];
      if (action.payload.length > 0) {
        selectedTab.graphData.yAxisTitleText = getYAxisTitleText(action.payload, selectedTab.messgroesseOptions);
        selectedTab.graphData.isNanoseconds = isDataWithNanoseconds(action.payload);
        const data = fillGapsWithNull(action.payload, selectedTab.fields.rekorder.klasse);
        selectedTab.graphData.series = transformDataToApexAxisChartSeries(data.data, selectedTab.messgroesseOptions, selectedTab.graphData.isNanoseconds, lineColors);
        selectedTab.graphData.singlePoints = [];//data.singlePoints;
      } else {
        selectedTab.graphData.yAxisTitleText = ""
        selectedTab.graphData.isNanoseconds = false;
        selectedTab.graphData.series = [];
      }
    },
    setGraphLoadingForTab: (state, action: PayloadAction<boolean>) => {
      state.tabs[state.selectedTab].graphLoading = action.payload;
    },
    setExportLoadingForTab: (state, action: PayloadAction<boolean>) => {
      state.tabs[state.selectedTab].exportLoading = action.payload;
    },
    setMultiView: (state, action: PayloadAction<multiViewType>) => {
      state.multiView = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setCsvDataForTab: (state, action: PayloadAction<{ data: any[], isUTCType: boolean }>) => {
      state.tabs[state.selectedTab].csvData =
        action.payload.data.length > 0
          ? new Array(Object.keys(action.payload.data[0])).concat(
            action.payload.data
              .map((e) =>
                Object.assign({}, e, {
                  zeit: nanosecondsTimeStampToLocalString(e.zeit),
                })
              )
              .map((e) => Object.values(e))
          )
          : [];
      (exportToCsvLocalTime("export.csv", state.tabs[state.selectedTab].csvData));
    },
    setMapBounds: (state) => {
      const messpunkte = state.tabs[state.selectedTab].fields.uw
        ? state.messpunkteSelectList.filter(
          (e) => e.umspannwerk === state.tabs[state.selectedTab].fields.uw
        )
        : state.messpunkteSelectList;
      state.tabs[state.selectedTab].mapBounds = messpunkte.map((x) => [
        x.breitengrad,
        x.laengengrad,
      ]);
    },
    setAnlagenTypen: (state, action: PayloadAction<Array<anlagentyp>>) => {
      state.anlagentypen = action.payload;
    },
    setAngeschlosseneAnlagen: (state, action: PayloadAction<Array<angeschlosseneAnlage>>) => {
      state.angeschlosseneAnlagen = action.payload;
    },
    setShowAngeschlosseneAnlagen: (state, action: PayloadAction<number>) => {
      state.tabs[action.payload].showAngeschlosseneAnlagen = !state.tabs[action.payload].showAngeschlosseneAnlagen;
    },
    selectFilteredMessgroessenOfGroup: (state, action: PayloadAction<{ groups: Array<string> | null, filter: string }>) => {
      const filter = action.payload.filter.toLowerCase();
      if (action.payload.groups) {
        const grouplessMessgroessen = state.tabs[state.selectedTab].fields.messgroesse.filter(x => !action.payload.groups?.includes(x.unterkategorie as string))
          .filter(x => state.tabs[state.selectedTab].messgroesseOptions.filter(m => m.unterkategorie == x.unterkategorie).length != state.tabs[state.selectedTab].fields.messgroesse.filter(m => m.unterkategorie == x.unterkategorie).length);
        state.tabs[state.selectedTab].fields.messgroesse = state.tabs[state.selectedTab].messgroesseOptions.filter(x => x.unterkategorie ? action.payload.groups?.includes(x.unterkategorie) : false);
        state.tabs[state.selectedTab].fields.messgroesse = state.tabs[state.selectedTab].fields.messgroesse.concat(grouplessMessgroessen);
        state.tabs[state.selectedTab].fields.messgroesse = state.tabs[state.selectedTab].fields.messgroesse.filter(x => x.beschreibung.toLowerCase().includes(filter) || x.feldname.toLowerCase().includes(filter));
      } else {
        state.tabs[state.selectedTab].fields.messgroesse = state.tabs[state.selectedTab].messgroesseOptions.filter(x => x.beschreibung.toLowerCase().includes(filter) || x.feldname.toLowerCase().includes(filter));
      }
    },
    setGespeicherteSuchen: (state, action: PayloadAction<Array<gespeicherteSuche>>) => {
      state.gespeicherteSuchen = action.payload;
    },
    loadGespeicherteSuche: (state, action: PayloadAction<TabData>) => {
      const newTab = emptyTab(state.tabs.length);
      newTab.rekorderOptions = action.payload.rekorderOptions;
      newTab.messgroesseOptions = action.payload.messgroesseOptions;
      newTab.fields = action.payload.fields;
      newTab.showAngeschlosseneAnlagen = action.payload.showAngeschlosseneAnlagen;
      newTab.resetDatePicker = false;
      state.tabs.push(newTab);
      state.selectedTab = state.tabs.length - 1;
      state.multiViewDisabled =
        state.tabs.length === 1 || state.tabs.length > multiViewTabsCount;
      state.multiView = state.multiViewDisabled ? multiViewType.single : state.multiView;

    },
    removeGespeicherteSucheFromState: (state, action: PayloadAction<number>) => {
      state.gespeicherteSuchen = state.gespeicherteSuchen.filter(x => x.id != action.payload);
    },
    setShowEreignisse: (state, action: PayloadAction<number>) => {
      state.tabs[action.payload].graphData.showEreignisse = !state.tabs[action.payload].graphData.showEreignisse;
    },
    addEreignisse: (state, action: PayloadAction<ereignis[]>) => {
      state.tabs[state.selectedTab].graphData.ereignisse = state.tabs[state.selectedTab].graphData.ereignisse.concat(action.payload);
    },
    clearEreignisse: (state) => {
      state.tabs[state.selectedTab].graphData.ereignisse = [];
    },
    setUmspannwerkLoading: (state, action: PayloadAction<boolean>) => {
      state.umspannwerkLoading = action.payload;
    },
    setMesspunktLoading: (state, action: PayloadAction<boolean>) => {
      state.messpunktLoading = action.payload;
    },
    setRekorderLoading: (state, action: PayloadAction<boolean>) => {
      state.rekorderLoading = action.payload;
    },
    setSucheLoading: (state, action: PayloadAction<boolean>) => {
      state.sucheLoading = action.payload;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    addGeflaggteMesswerte: (state, action: PayloadAction<messwertLogGeflaggt[]>) => {
      state.tabs[state.selectedTab].graphData.geflaggteMesswerte = state.tabs[state.selectedTab].graphData.geflaggteMesswerte.concat(action.payload);
    },
    clearGeflaggteMesswerte: (state) => {
      state.tabs[state.selectedTab].graphData.geflaggteMesswerte = [];
    },
    setViewMode: (state, action: PayloadAction<string>) => {
      state.viewMode = action.payload;
    },
  },
});

//Selectors
//======================================================================
export const selectUWs = (state: RootState): string[] =>
  state.netzwirtschaft.uwsList;

//TODo get rid of undifined
export const selectMesspunkte = (state: RootState): messstelle[] => {
  return state.netzwirtschaft.tabs[state.netzwirtschaft.selectedTab].fields.uw
    ? state.netzwirtschaft.messpunkteSelectList.filter(
      (e) =>
        e.umspannwerk ===
        state.netzwirtschaft.tabs[state.netzwirtschaft.selectedTab].fields.uw
    )
    : state.netzwirtschaft.messpunkteSelectList;
};

export const selectSelectedTab = (state: RootState): number =>
  state.netzwirtschaft.selectedTab;
export const selectTabs = (state: RootState): TabData[] =>
  state.netzwirtschaft.tabs;
export const selectMultiView = (state: RootState): multiViewType =>
  state.netzwirtschaft.multiView;
export const selectMultiViewDisabled = (state: RootState): boolean =>
  state.netzwirtschaft.multiViewDisabled;
export const selectAnlagentypen = (state: RootState): Array<anlagentyp> => state.netzwirtschaft.anlagentypen;
export const selectAngeschlosseneAnlagen = (state: RootState): Array<angeschlosseneAnlage> => state.netzwirtschaft.angeschlosseneAnlagen;
export const selectGespeicherteSuchen = (state: RootState): Array<gespeicherteSuche> => state.netzwirtschaft.gespeicherteSuchen;
export const selectMesspunktLoading = (state: RootState): boolean => state.netzwirtschaft.messpunktLoading;
export const selectUmspannwerkLoading = (state: RootState): boolean => state.netzwirtschaft.umspannwerkLoading;
export const selectRekorderLoading = (state: RootState): boolean => state.netzwirtschaft.rekorderLoading;
export const selectSucheLoading = (state: RootState): boolean => state.netzwirtschaft.sucheLoading;
export const selectListLoading = (state: RootState): boolean => state.netzwirtschaft.listLoading;
export const selectViewMode = (state: RootState): string => state.netzwirtschaft.viewMode;

//Actions
//=====================================================================
export const onDropDownSelect = (
  payload: fieldUpdate
): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
    dispatch(updateField(payload));
    switch (payload.field) {
      case "uw": {
        dispatch(setMapBounds());
        break;
      }
      case "messpunkt": {
        if (!payload.value) break;
        dispatch(setMesspunktLoading(true));
        getWithAuth("DATENFELDKATEGORIEID", {}, payload.value.toString())
          .then((response: recorder[]) => {
            dispatch(setRekorderOptionsForTab(response.filter(x => x.kategorie)));
          })
          .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
          }).finally(() => dispatch(setMesspunktLoading(false)));
        break;
      }
      case "rekorder": {
        if (!payload.value) break;
        dispatch(setRekorderLoading(true));
        getWithAuth("DATENFELDER", payload.value)
          .then((response: messgroesse[]) => {
            dispatch(setMessgroesseOptionsForTab(response));
          })
          .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
          }).finally(() => dispatch(setRekorderLoading(false)));

        const recorderData = <recorder>payload.value;

        const messstelleListe = []
        messstelleListe.push(getState().netzwirtschaft.tabs[
          getState().netzwirtschaft.selectedTab
        ].fields.messpunkt);
        const parameters = {
          hersteller: recorderData.hersteller,
          klasse: recorderData.klasse,
        };
        dispatch(setListLoading(true));
        postWithAuth("FIRSTANDLASTMESSWERT", parameters, messstelleListe)
          .then((response) => {
            dispatch(
              updateField({
                field: "ersteDaten",
                value: new Date(+response.firstTimeStamp / 1000000)
              })
            ); //Divide by 1000000 because js cant handle nano seconds
            if (response.lastTimeStamp > 0) dispatch(updateFieldWithPickerResetCheck({ field: "startDatum", value: new Date(+response.lastTimeStamp / 1000000 - 24 * 60 * 60000) }));
            dispatch(
              updateField({
                field: "letzteDaten",
                value: new Date(+response.lastTimeStamp / 1000000)
              })
            ); //Divide by 1000000 because js cant handle nano seconds
            if (response.lastTimeStamp > 0) dispatch(updateFieldWithPickerResetCheck({ field: "endDatum", value: new Date(+response.lastTimeStamp / 1000000) }));
          })
          .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
          }).finally(() => dispatch(setListLoading(false)));
        break;
      }
    }
  };

export const getUWS = () => (dispatch: AppDispatch): void => {
  dispatch(setUmspannwerkLoading(true));
  getWithAuth("MESSSTELLE", {})
    .then((response: Array<messstelle>) => {
      const responseFiltered = response.filter((x) => x.hatDaten);
      if (responseFiltered.length > 0) {
        const umspannwerke: Array<string> = [
          ...new Set(
            responseFiltered
              .map((a) => a.umspannwerk)
              .sort((a, b) => a.localeCompare(b))
          ),
        ];
        dispatch(setUWS(umspannwerke));
        dispatch(setMesspunkte(responseFiltered));
        dispatch(setMapBounds());
      }
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    });
  getWithAuth("ANLAGENTYPEN", {})
    .then((response: Array<anlagentyp>) => {
      dispatch(setAnlagenTypen(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    });
  getWithAuth("ANLAGEN", {})
    .then((response: Array<angeschlosseneAnlage>) => {
      dispatch(setAngeschlosseneAnlagen(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setUmspannwerkLoading(false)));
};

export const getGraphData = (tabData: TabData) => (
  dispatch: AppDispatch
): void => {
  dispatch(setGraphDataForTab([]));
  dispatch(clearEreignisse());
  dispatch(clearGeflaggteMesswerte());
  dispatch(setGraphLoadingForTab(true));
  
  const startDate = new Date(tabData.fields.startDatum).toISOString();
  const endDate = new Date(tabData.fields.endDatum).toISOString();
  const messwertePromise = postWithAuth(
    "MESSWERT",
    {
      from: startDate,
      to: endDate,
      messstelle: tabData.fields.messpunkt,
      hersteller: tabData.fields.rekorder.hersteller,
      klasse: tabData.fields.rekorder.klasse,
    },
    tabData.fields.messgroesse.map(mg => mg.feldname).join(",")
  );
  const netzereignissePromise = getWithAuth(
    "NETZEREIGNIS",
    {
      from: startDate,
      to: endDate,
      messstelle: tabData.fields.messpunkt,
    },
  );
  const pqereignissePromise = getWithAuth(
    "PQEREIGNIS",
    {
      from: startDate,
      to: endDate,
      messstelle: tabData.fields.messpunkt,
    },
  );
  const oszilloskopPromise = postWithAuth(
    "MESSWERTLOG",
    {
      hersteller: "Eberle",
      datenklasse: "creca",
      from: startDate,
      to: endDate,
    }, tabData.fields.messpunkt,
  );
  const effektivwertePromise = postWithAuth(
    "MESSWERTLOG",
    {
      hersteller: "Eberle",
      datenklasse: "creca",
      from: startDate,
      to: endDate,
    }, tabData.fields.messpunkt,
  );
  const geflaggteMesswertePromise: Promise<Array<messwertLogGeflaggt>> =
    postJsonWithAuth("POSTMESSWERTEGEFLAGGT", {},
      {
        start: startDate,
        end: endDate,
        status: [messwertLogGeflaggtStatus.verworfen],
        messstellen: [tabData.fields.messpunkt]
      });
  Promise.all([messwertePromise, netzereignissePromise, pqereignissePromise, oszilloskopPromise, effektivwertePromise, geflaggteMesswertePromise])
    .then(([messwerteResponse, netzereignisseReponse, pqereignisseResponse, oszilloskopResponse, effektivwerteResponse, geflaggteMesswerteResponse]:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [Array<any>, Array<netzereignis>, Array<pqereignissemessstelle>, Array<messwertLog>, Array<messwertLog>, Array<messwertLogGeflaggt>]) => {
      if (messwerteResponse.length > 0) {
        dispatch(setGraphDataForTab(messwerteResponse));
        let ereignisse: ereignis[] = netzereignisseReponse.map(x => ({ typ: ereignistyp.netzereignis, zeit: new Date(x.zeit).getTime() * 1000000, ende: new Date(x.ende).getTime() * 1000000, beschreibung: x.ereignistext }));
        if (pqereignisseResponse.length > 300) {
          pqereignisseResponse = pqereignisseResponse.slice(0, 300);
          dispatch(openSnackbar({
            message: "Es werden nur die 300 ersten PQ-Ereignisse angezeigt. Wählen Sie einen kleineren Zeitraum, um alle anzuzeigen.",
            severity: "info",
            duration: 6000,
          }));
        }
        ereignisse = ereignisse.concat(pqereignisseResponse.map(x => ({ typ: ereignistyp.pqereignis, zeit: x.zeit, ende: x.ende, beschreibung: x.beschreibung + " " + x.typ + " " + (x.harmonische ?? "") })));
        if (oszilloskopResponse.length > 300) {
          oszilloskopResponse = oszilloskopResponse.slice(0, 300);
          dispatch(openSnackbar({
            message: "Es werden nur die 300 ersten Oszilloskopaufzeichnungen angezeigt. Wählen Sie einen kleineren Zeitraum, um alle anzuzeigen.",
            severity: "info",
            duration: 6000,
          }));
        }
        ereignisse = ereignisse.concat(oszilloskopResponse.map(x => ({ typ: ereignistyp.stoerschrieb, zeit: x.start, ende: x.ende, beschreibung: x.datenklasse })));
        if (effektivwerteResponse.length > 300) {
          effektivwerteResponse = effektivwerteResponse.slice(0, 300);
          dispatch(openSnackbar({
            message: "Es werden nur die 300 ersten Effektivwertaufzeichnungen angezeigt. Wählen Sie einen kleineren Zeitraum, um alle anzuzeigen.",
            severity: "info",
            duration: 6000,
          }));
        }
        ereignisse = ereignisse.concat(effektivwerteResponse.map(x => ({ typ: ereignistyp.stoerschrieb, zeit: x.start, ende: x.ende, beschreibung: x.datenklasse })));
        dispatch(addEreignisse(ereignisse));
        dispatch(addGeflaggteMesswerte(geflaggteMesswerteResponse));
      }
      else dispatch(openSnackbar({
        message: "Für den gewählten Zeitraum liegen keine Daten vor.",
        severity: "info",
        duration: 6000,
      }));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setGraphLoadingForTab(false)));
};

export const getCsvData = (tabData: TabData, isUTCType: boolean) => (
  dispatch: AppDispatch
): void => {
  setCsvDataForTab({ data: [], isUTCType: isUTCType });
  dispatch(setExportLoadingForTab(true));
  const startDate = new Date(tabData.fields.startDatum).toISOString();
  const endDate = new Date(tabData.fields.endDatum).toISOString();
  postWithAuth(
    "MESSWERT",
    {
      from: startDate,
      to: endDate,
      messstelle: tabData.fields.messpunkt,
      hersteller: tabData.fields.rekorder.hersteller,
      klasse: tabData.fields.rekorder.klasse,
    },
    tabData.fields.messgroesse.map(x => x.feldname).join(",")
  )
    .then((response) => {
      response.length > 0
        ? dispatch(setCsvDataForTab({ data: response, isUTCType: isUTCType }))
        : dispatch(
          openSnackbar({
            message: "Für den gewählten Zeitraum liegen keine Daten vor.",
            severity: "info",
            duration: 6000,
          })
        );
      dispatch(setExportLoadingForTab(false));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
      dispatch(setExportLoadingForTab(false));
    });
};

export const getExcelData = (tabData: TabData) => (
  dispatch: AppDispatch
): void => {
  dispatch(setExportLoadingForTab(true));
  const startDate = new Date(tabData.fields.startDatum).toISOString();
  const endDate = new Date(tabData.fields.endDatum).toISOString();
  postWithAuth(
    "MESSWERTEXPORTEXCEL",
    {
      from: startDate,
      to: endDate,
      messstelleName: tabData.fields.messpunkt,
      hersteller: tabData.fields.rekorder.hersteller,
      klasse: tabData.fields.rekorder.klasse,
    },
    tabData.fields.messgroesse.map(x => x.feldname).join(",")
  )
    .then((response) => {
      const link = document.createElement('a');
      //eslint-disable-next-line scanjs-rules/assign_to_href
      link.href = response;
      document.body.appendChild(link);
      link.click();
      dispatch(setExportLoadingForTab(false));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
      dispatch(setExportLoadingForTab(false));
    });
};



export const saveSearch = (name: string, wert: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setSucheLoading(true));
  postJsonWithAuth("KONFIG", {}, {
    id: 0,
    kategorie: "gespeicherteSuche",
    name: name,
    wert: wert
  })
    .then(() => {
      dispatch(getGespeicherteSuchen());
      dispatch(
        openSnackbar({
          message: `Suche ${name} wurde gespeichert.`,
          severity: "success",
          duration: 6000,
        })
      );
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSucheLoading(false)));
};

export const getGespeicherteSuchen = () => (dispatch: AppDispatch): void => {
  dispatch(setSucheLoading(true));
  getWithAuth("GESPEICHERTESUCHEN", {})
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .then((response: Array<any>) => {
      const suchen: gespeicherteSuche[] = response.map(x => ({ id: x.id, name: x.name, wert: JSON.parse(x.wert), istLoeschbar: x.istLoeschbar }));
      dispatch(setGespeicherteSuchen(suchen));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSucheLoading(false)));
};

export const deleteGespeicherteSuche = (suche: gespeicherteSuche): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setSucheLoading(true));
  deleteWithAuth("KONFIGID", {}, suche.id.toString())
    .then(() => {
      dispatch(removeGespeicherteSucheFromState(suche.id));
      dispatch(openSnackbar({
        message: `${suche.name} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setSucheLoading(false)));
};

export const {
  setSelectedTab,
  addTab,
  deleteTab,
  setUWS,
  setMesspunkte,
  selectFilteredMessgroessenOfGroup,
  setRekorderOptionsForTab,
  setMessgroesseOptionsForTab,
  updateField,
  setGraphDataForTab,
  setGraphLoadingForTab,
  setExportLoadingForTab,
  setMultiView,
  setCsvDataForTab,
  setMapBounds,
  setAnlagenTypen,
  setAngeschlosseneAnlagen,
  setShowAngeschlosseneAnlagen,
  setGespeicherteSuchen,
  loadGespeicherteSuche,
  removeGespeicherteSucheFromState,
  setShowEreignisse,
  addEreignisse,
  clearEreignisse,
  setUmspannwerkLoading,
  setMesspunktLoading,
  setRekorderLoading,
  setSucheLoading,
  setListLoading,
  addTabNotResetDate,
  updateFieldWithPickerResetCheck,
  addGeflaggteMesswerte,
  clearGeflaggteMesswerte,
  setViewMode,
} = netzwirtschaftSlice.actions;
export default netzwirtschaftSlice.reducer;
